import type { Polygon } from 'geojson';

import type { FloodJob, FloodJobStep, SarAoiAnalysisKind } from '@/mocks/floodJobs';
import { DEFAULT_STAGE_WEIGHTS, DEFAULT_TARGET_DURATION_MS } from '@/mocks/floodJobs';

function pickStr(row: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function pickNum(row: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return undefined;
}

function mapGeoStatus(raw: string): FloodJob['status'] {
  const s = raw.toLowerCase();
  if (/completed|succeeded|success|done/.test(s)) return 'completed';
  if (/failed|error/.test(s)) return 'failed';
  if (/running|processing|active|in_progress|working/.test(s)) return 'running';
  if (/cancel|canceled|cancelled/.test(s)) return 'failed';
  return 'queued';
}

export function mapSarAnalyzeResponseToFloodJob(opts: {
  api: Record<string, unknown>;
  name: string;
  aoiLabel: string;
  aoiPolygon?: Polygon;
  aoiAreaKm2?: number;
  parameters: FloodJob['parameters'];
  submittedBy: string;
  analysisKind: SarAoiAnalysisKind;
  stepLabels: string[];
}): FloodJob {
  const jobId = pickStr(opts.api, ['job_id', 'id']) || `sar-${Date.now()}`;
  const statusRaw = pickStr(opts.api, ['status']);
  const status = mapGeoStatus(statusRaw || 'queued');

  const progressPct =
    pickNum(opts.api, [
      'progress_percent',
      'progress_pct',
      'progress',
      'completion_pct',
      'percent_complete',
    ]) ?? (status === 'completed' ? 100 : 0);

  const rawRatio = pickNum(opts.api, ['flood_area_pixel_ratio']);
  const ratio = rawRatio ?? 0.15 + Math.random() * 0.3;
  const areaEstimate =
    opts.aoiAreaKm2 != null && ratio >= 0 ? Math.round(opts.aoiAreaKm2 * ratio * 100) / 100 : 0;
  const accuracyScore =
    rawRatio != null
      ? Math.min(100, Math.round(ratio * 10_000) / 100)
      : Math.round(85 + Math.random() * 10);

  const steps: FloodJobStep[] = opts.stepLabels.map((name, i) => {
    let st: FloodJobStep['status'] = 'pending';
    if (status === 'completed') st = 'done';
    else if (status === 'failed') {
      if (i === 0) st = 'done';
      else if (i === 1) st = 'error';
    } else if (status === 'running') {
      if (i < 2) st = 'done';
      else if (i === 2) st = 'running';
    }
    return { name, status: st };
  });

  const now = new Date().toISOString();
  const errorMessage =
    pickStr(opts.api, ['error_message', 'errorMessage', 'failure_reason', 'failureReason']) ||
    undefined;

  return {
    id: jobId,
    name: opts.name,
    status,
    progress: Math.min(100, Math.max(0, Math.round(progressPct))),
    aoi: opts.aoiLabel,
    aoiPolygon: opts.aoiPolygon,
    aoiAreaKm2: opts.aoiAreaKm2,
    startedAt: now,
    finishedAt: status === 'completed' || status === 'failed' ? now : undefined,
    ...(errorMessage ? { errorMessage } : {}),
    submittedBy: opts.submittedBy,
    parameters: opts.parameters,
    runtime: {
      targetDurationMs: DEFAULT_TARGET_DURATION_MS[opts.analysisKind],
      stageWeights: DEFAULT_STAGE_WEIGHTS[opts.analysisKind],
    },
    steps,
    results:
      status === 'completed'
        ? {
            floodedAreaKm2: areaEstimate,
            accuracyScore,
          }
        : undefined,
  };
}
